import { CUSTOM_CARD_TYPES } from "./components/cards/constants";
import { BlogCategoryEntity } from "./screens/news/types";
import {
  ComicCategoryEntity,
  ComicFacetGroupEntity,
  ComicUserReadingStatusEntity,
  InitResponsePayloadResults,
} from "./services/gc/types";
import { ComicContributorTypeEntity } from "./services/gc/types/comicContributorTypeEntity";
import { ReactionTypeEntity } from "./services/gc/types/reactionTypeEntity";

export enum BrowseTabType {
  comics = 0,
  releases = 1,
  creators = 2,
  bundles = 3,
}

export type ConversationUser = {
  entity_type: "User";
  id: number;
  name: string;
  url: string;
  avatar_url: string;
  avatar_small_url: string;
  avatar_tiny_url: string;
};

export type NotificationUser = {
  entity_type: "NotificationUser";
  id: number;
  name: string;
  url: string;
  avatar_url: string;
  avatar_small_url: string;
  avatar_tiny_url: string;
};

export interface ReactionType {
  entity_type: string;
  id: number;
  slug: string;
  name: string;
  display_name: string;
  display_order: number;
  is_active: number;
}

export interface Reaction {
  entity_type: string;
  id: number;
  context_entity_type_id: number;
  context_entity_id: number;
  reaction_type_id: number;
  value: number;
  is_clicked: boolean;
  reaction_type: ReactionType;
}

export type ContextXTag = {
  context_entity_id: number;
  context_entity_type_id: number;
  entity_type: string;
  id: number;
  name: string;
  tag_id: number;
};

export type Article = {
  id: number;
  title: string;
  description: string;
  published_time: string;
  preview_image_url: string;
  author: User;
  total_comments: number;
  url: string;
  share_url: string;
  entity_type: "News";
  domain: string;
  reactions?: Reaction[];
  category?: BlogCategoryEntity;
  time_ago?: string;
  body?: string;
  parsed_body?: string;
  tags?: Record<number, ContextXTag>;
  youtube_id?: string;
  can_edit: boolean;
  edit_url: string;
  is_open?: boolean;
};

export type ArtistEntityType = {
  entity_type: string;
  id: number;
  display_order: number;
  name: string;
  plural_name: string;
  slug: string;
  description: string | null;
  is_active: number;
};

export type Artist = {
  entity_type: "Artist";
  id: number;
  name: string;
  creator_user_id: number;
  website: string;
  description: string;
  lang: string;
  avatar_image_id: number;
  banner_image_id: number | null;
  roman_name: string;
  twitter: string;
  facebook: string;
  accepted_date: string;
  total_favorites: number;
  total_news: number;
  total_projects: number;
  total_releases: number;
  business_name: string | null;
  ga_id: string;
  artist_type_id: number;
  last_activity: string;
  total_posts: number;
  country_id: string;
  monthly_donation_goal: string;
  monthly_donation_amount: number;
  accept_donations: number;
  user_is_supporter: boolean;
  user_is_following: boolean;
  artist_actions: any[];
  artist_type: ArtistEntityType;
  type_display: string;
  lang_display: string;
  url: string;
  country_display: string;
  donate_url: string;
  secure_donate_url: string;
  edit_url: string;
  delete_url: string;
  admin_edit_url: string;
  income_url: string;
  incentive_codes_url: string;
  add_fan_url: string;
  avatar_url: string;
  avatar_small_url: string;
  avatar_tiny_url: string;
  banner_url: string;
  flag_url: string;
  twitter_url: string;
  website_url: string;
  website_display: string;
  share_url: string;
  facebook_display: string;
  can_edit: boolean;
  gold_details: string | null;
  bundles: Bundle[];
  channel_key?: string;
};

export type ArtistUser = {
  id: number;
  artist_id: number;
  artist_role_id: number;
  artist_user_status_id: number;
  user_id: number;
  artist: Artist;
  display_name: string;
};

export type ReadStatus = {
  comic_id: number;
  last_page_read: number;
  last_read_date: string;
  is_complete: boolean;
  count_read_completed: number;
  count_read: number;
  count_pages_read: number;
  lang_id: string;
  order: number;
  read_progress_percentage: number;
  uuid: string;
};

export type Comic = {
  entity_type: "Comic";
  id: number;
  url: string;
  share_url: string;
  read_url: string;
  name: string;
  slug: string;
  bundles?: Bundle[];
  // localized_name: string;
  description: string;
  lang: string;
  year: number;
  category_name: string;
  status: number;
  status_name: "ongoing" | "updated";
  total_releases: number;
  total_chapters: number;
  total_pageviews: number;
  total_favorites: number;
  total_published_releases: number;
  total_published_pages: number;
  pages_viewed: number;
  is_active: boolean;

  can_edit: boolean;
  edit_url: string;
  image_url: string;
  image_small_url: string;
  image_tiny_url: string;
  image_medium_url: string;
  page_thumbnails: string[];

  user_read_status: ReadStatus | null;

  is_favorite: boolean;
  is_free: boolean;

  artist: Artist;

  // TODO `include_match` counterpart?
  relevance_match: number;

  update_time: string;
  comic_langs?: ComicLang[];
  comic_type_id: number;
  comic_format_id: number;
  reading_direction: string;
  comic_update_frequency: number;
  comic_update_period: number;
  last_published_release_published_time: string;
  is_augmented: 0 | 1;
  type_display: string;
  library_status?: LibraryStatusEntity;
  comic_facets?: ComicFacetEntity[];
  leaving_soon_date?: string;

  contributors: ComicContributorEntity[];
  releases: Release[];

  section_id: number;
  first_release_uuid?: string;

  crowdfunding_campaign?: CrowdfundingCampaignEntity;
  is_open?: boolean;
  count_gold?: number;
};

export type CrowdfundingCampaignEntity = {
  entity_type: "CrowdfundingCampaign";
  id: number;
  comic_id: number;
  crowdfunding_service_id: number;
  url: string;
  start_time: string;
  end_time: string;
  is_visible: boolean;
  goal_amount: string;
  was_successful: boolean | null;
  is_active: boolean;
  updater_id: number;
  update_time: string;
  create_time: string;
  creator_id: number;
  local_start_time: string;
  local_end_time: string;
  crowdfunding_service: CrowdfundingServiceEntity;
  time_remaining: "1 month, 2 days, 23 hours, 1 minute";
};

export type CrowdfundingServiceEntity = {
  entity_type: "CrowdFundingService";
  id: number;
  display_name: string;
  is_active: boolean;
};

export type ComicContributorEntity = {
  entity_type: string;
  id: number;
  comic_contributor_type_id: number;
  comic_id: number;
  context_entity_type_id: null;
  context_entity_id: null;
  name: string;
  is_active: boolean;
  creator_id: number;
  create_time: string;
  comic_contributor_type: ComicContributorTypeEntity;
};

export type LibraryStatusEntity = {
  entity_type: string;
  comic_id: number;
  user_id: number;
  comic_user_reading_status_id: number;
  disable_notifications: number;
  disable_emails: number;
  create_time?: string;
  last_update?: string;
};

export type Release = {
  id: number;
  entity_type: "Release";
  image_url: string;
  image_small_url: string;
  thumbnail_url: string;
  localized_name: string;
  localized_comic_name: string;
  title: string;
  url: string;
  read_url: string;
  share_url: string;
  lang_display: string;
  lang_id: string;

  user_read_status: ReadStatus;
  artist: Artist;
  comic?: Comic;
  comic_id: number;
  total_pageviews: number;
  total_comments: number;
  page_count: number;
  update_time: string; // ISODate

  description: string;
  reactions: ReactionSummaryEntity[];
  page_objects: Page[];
  has_next_release: boolean;
  has_previous_release: boolean;
  is_vertical_format: boolean;
  is_free: boolean;
  is_nsfw: boolean;
  comic_facets: ComicFacetEntity[];
  chapter: number;
  chapter_type: string;
  time_ago: string;
  is_augmented: boolean;
  augment_blur: number;
  background_color: string;

  free_page_count?: number;

  next_release_read_url: string;
  previous_release_read_url: string;
  pdf_download_sale_available: boolean;
  previous_release_order?: number;
  next_release_order: number;
  key?: string;
  order: number;
  price: Price;
  release_user_purchase: ReleaseUserPurchase | null;
  // I will figure this out and correct this after discussing with Alex - Cosmo
  access_token_download: null | {};
  tracked_website_url: string;

  paid_access_thumbnail_blur: boolean;
  can_edit: boolean;

  nice_published_time?: string;

  edit_url: string;
  page_thumbnails?: PageThumbnailEntity[];

  premium_only: boolean;
  ppb_price_point?: number;
  ppb_gold_price_point?: number;

  comic_slug?: string;
  is_open: boolean;
  ppb_sale_price_point?: number;
  ppb_gold_sale_price_point?: number;

  bundles?: Bundle[];

  // Print-on-Demand fields
  is_pod_available?: boolean;
  pod_price?: number;
  pod_product_id?: number;
  pod_product_name?: string;
};

export type BundleItem = {
  entity_type: string;
  release_id: number;
  release_lang_id: string;
  release_chapter_type: string;
  release_chapter: string;
  release_title: string;
  release_uuid: string;
  release_order: number;
  release_page_count: number;
  release_free_page_count: number;
  release_total_pageviews: number;
  release_force_is_free: number;
  release_is_augmented: number;
  release_is_nsfw: number;
  release_isbn: string | null;
  release_published_time: string;
  release_public_time_delta: string | null;
  release_public_time: string | null;
  comic_cover_image_id: number;
  comic_slug: string;
  comic_id: number;
  comic_category_id: number;
  comic_format_id: number;
  comic_type_id: number;
  comic_name: string;
  list_item_id: number;
  list_id: number;
  list_item_display_order: number;
  artist_id: number;
  artist_roman_name: string;
  artist_name: string;
  artist_avatar_image_id: number | null;
  ppb_price_point: number;
  user_owns_release: boolean;
  comic_image_url: string;
  comic_image_small_url: string;
  comic_image_tiny_url: string;
  artist_avatar_url: string | null;
  thumbnail_url: string;
  read_url: string;
  comic_url: string;
  artist_url: string;
};

export type BundleOwner = {
  type: string;
  name: string;
  avatar_url: string;
  url: string;
};

export type BundleItemPriceCount = {
  ppb_price_point: number;
  count: number;
};

export type Bundle = {
  entity_type: string;
  id: number;
  list_id: number;
  list_type_id: number;
  owner_type_id: number;
  owner_id: number;
  display_name: string;
  description: string;
  is_public: number;
  uuid: string;
  local_start_time: string;
  local_end_time: string;
  url: string;
  api_url: string;
  user_owns_all_items: boolean;
  expires_on: string | null;
  ppb_price_point: number;
  ppb_gold_price_point: number;
  ppb_sale_price_point: number | null;
  ppb_gold_sale_price_point: number | null;
  price: Price;
  gold_price: Price;
  contributors: any;
  comic_facets?: ComicFacetEntity[];
  owner: BundleOwner;
  total_series: number;
  total_items: number;
  total_pages: number;
  total_pageviews: number;
  total_owned: number;
  item_price_points_count: BundleItemPriceCount[];
  items: BundleItem[];
};

export enum ReleasePricingType {
  Free = "free",
  Purchased = "purchased",
  UnlockedWithGold = "unlockedWithGold",
  Regular = "regular",
  AvailableGoldDiscount = "availableGoldDiscount",
  GoldDiscount = "goldDiscount",
  FreeWithGold = "freeWithGold",
  PpbOrFreeWithGold = "ppbORFreeWithGold",
  OnSale = "onSale",
  OnSaleAndAvailableGoldDiscount = "onSaleAndAvailableGoldDiscount",
  OnSaleWithGoldDiscount = "onSaleWithGoldDiscount",
  RegularWithAvailableOnGoldSale = "regularWithAvailableOnGoldSale",
  OnGoldSale = "onGoldSale",
  OnSaleOrFreeWithGold = "onSaleOrFreeWithGold",
  onSaleWithAvailableOnGoldSale = "onSaleWithAvailableOnGoldSale",
}

export type PPBPricingData = {
  is_free?: boolean;
  ppb_price_point?: number;
  ppb_gold_price_point?: number;
  ppb_sale_price_point?: number;
  ppb_gold_sale_price_point?: number;
  release_user_purchase?: ReleaseUserPurchase;
  premium_only?: boolean;
};

export interface ReleasePricingInfo {
  type: ReleasePricingType;

  // Core prices (in dollars, not cents)
  regularPrice?: number;
  goldPrice?: number;
  salePrice?: number;
  goldSalePrice?: number;

  isOnSale?: boolean;
  isUnlocked?: boolean;
  isPremiumOnly?: boolean;
  hasGoldDiscount?: boolean;

  pricePointToUse?: number;
}

export type PageThumbnailEntity = {
  alt_text: string;
  aspect_x: number;
  page: number;
  read_url: string;
  url: string;
};

export type ComicFacetEntity = {
  entity_type: string;
  id: number;
  comic_id: number;
  comic_facet_group_option_id: number;
  comic_facet_group_id: number;
  is_active: boolean;
  comic_facet_group_options?: ComicFacetGroupEntity[];
};

export type ReactionSummaryEntity = {
  entity_type: string;
  id: number;
  context_entity_type_id: number;
  context_entity_id: number;
  reaction_type_id: number;
  value: number;
  is_clicked: boolean;
  reaction_type: ReactionTypeEntity;
};

export type ContentSectionCustomCard = {
  entity_type: "ContentSectionCustomCard";
  id: number;
  content_section_id: number;
  display_order: number;
  display_name: string;
  description: string | null;
  card_type:
    | "hero-featured"
    | "featured-row-carousel"
    | "featured-roadblock-carousel";
  eyebrow: string;
  comic_facet_group_option_id: 57 | 58 | 99 | 100 | 101 | 102;
  cta_type: "notify" | "deeplink" | "url";
  cta_url: string | null;
  context_entity_type_id: number | null;
  context_entity_id: number | null;
  start_time: string | null;
  end_time: string | null;
  is_public: boolean;
  images: Image[];
  artist: Artist | null;
  comic: Comic | null;
  release: Release | null;
  bundle: Bundle | null;
  left_text?: string;
  right_text?: string;
  left_element?: "image" | "text" | "cta";
  right_element?: "image" | "text" | "cta";
  center_text?: string;
  center_element?: "image" | "text" | "cta";
};

export type Image = {
  entity_type: "Image";
  id: number;
  image_type_id: number;
  context_entity_id: number;
  image_asset_id: number;
  display_order: number;
  slug: string | null;
  image_type: ImageType;
  large_url: string;
  medium_url: string;
  small_url: string;
};

export type ImageType = {
  entity_type: "ImageType";
  id: number;
  slug: string;
  display_name: string;
  sizes: ImageTypeSize[];
};

export type ImageTypeSize = {
  entity_type: "ImageTypeSize";
  id: number;
  image_type_id: number;
  slug: string;
  display_name: string;
  width: number;
  height: number;
  quality: number;
  mime_type: string;
  extension: string;
};

export type DisplayFlags = {
  include_cover_image?: boolean;
  include_name?: boolean;
  include_title?: boolean;
  include_follow?: boolean;
  include_owner_name?: boolean;
  include_author?: boolean;
  include_description?: boolean;
  include_comic_status?: boolean;
  include_crowdfunding?: boolean;
  include_domain?: boolean;
  include_position_num?: boolean;
  include_progress_meter?: boolean;
  include_ribbon_badge?: boolean;
  include_bubble_badge?: boolean;
  include_favorite?: boolean;
  include_facet?: boolean;
  include_total_pages?: boolean;
  include_total_views?: boolean;
  include_total_favorites?: boolean;
  include_total_series?: boolean;
  include_total_releases?: boolean;
  include_total_comments?: boolean;
  include_last_updated?: boolean;
  include_update?: boolean;
  include_posted_time?: boolean;
  include_share?: boolean;
  include_add_library_cta?: boolean;
  include_match?: boolean;
  include_flag?: boolean;
  include_header?: boolean;
  include_post_details?: boolean;
  include_chapter_num?: boolean;
  include_footer?: boolean;
  include_reading_status_button?: boolean;
  include_view_all?: boolean;
  include_gold_badge?: boolean;
};

export type CustomCardTypeValues =
  (typeof CUSTOM_CARD_TYPES)[keyof typeof CUSTOM_CARD_TYPES];

export type LayoutStyle = {
  context:
    | "comics"
    | "creators"
    | "news"
    | "releases"
    | "bundle"
    | CustomCardTypeValues;
  mode: "list" | "grid" | "horizontal" | "calendar";
  size: "xl" | "s" | "c_1" | "l" | "m" | "r_list" | "xs" | "v2";
  rows: number;
  corners_top: "left" | "right";
  corners_bottom: "left" | "right";
  section_is_inset: boolean;
};

export type ToggleComicFollowHandler = (
  comicSlug: string,
  langId: string,
  chapter: string,
) => void;

export type ToggleComicLikeHandler = (
  comicSlug: string,
  isFavorite: boolean,
) => void;

export type ToggleCreatorFollowHandler = (
  id: number,
  willBeNotified: boolean,
) => void;

export type SectionLayout = {
  style: LayoutStyle;
  display: DisplayFlags;
  hide_item_count?: boolean;
};

export type Section = {
  entity_type:
    | "ContentSection"
    | "Comic"
    | "Artist"
    | "ArtistType"
    | "News"
    | "ContentSectionCustomCard"
    | "ReactionSummary"
    | "ReactionType"
    | "ContextXTag"
    | "BlogCategory"
    | "User"
    | "Country";
  meta: {
    layout: SectionLayout;
    config: {
      guest_ids?: string[];
      guest_icon_url?: string;
      pinned_message?: string;
    };
  };
  id: number;
  uuid: string;
  display_name: string;
  display_order: number;
  comics: Comic[];
  artists: Artist[];
  articles: Article[];
  releases: Release[];
  cards: ContentSectionCustomCard[];
  bundles: Bundle[];
  description?: string;
  share_url: string;
  results_url: string;
  source_type: string;
  start_time?: null | string;
  end_time?: null | string;
  requires_auth?: boolean;
  requires_sub?: boolean;
};

export type ContentCardProps = {
  content:
    | Comic
    | Artist
    | Article
    | Release
    | Bundle
    | ContentSectionCustomCard;
  layout: {
    style: LayoutStyle;
    display: DisplayFlags;
  };
  fullWidth?: boolean;
  section?: Section;
  dontOpenInfo?: boolean;
  index?: number;
  sectionIndex?: number;
  area?: string;
};

export type LayoutScaleProps = {
  scaleX?: number;
  scaleY?: number;
};

export type ComicContentCardProps = ContentCardProps & {
  content: Comic;
  position?: number;
  onToggleFollow?: () => void;
  scale?: LayoutScaleProps;
};

export type ArtistContentCardProps = ContentCardProps & {
  content: Artist;
  onToggleFollow?: () => void;
};

export type ArticleContentCardProps = ContentCardProps & {
  content: Article;
};

export type ReleaseContentCardProps = ContentCardProps & {
  content: Release;
  onToggleFavorite?: ToggleComicLikeHandler;
  onToggleFollow?: ToggleComicFollowHandler;
  isLocked?: boolean;
  wasPurchased?: boolean;
  showPurchasedBadge?: boolean;
  goldUnlocked?: boolean;
  hideBackdrop?: boolean;
  showIncludedLabel?: boolean;
};

export type CustomContentCardProps = ContentCardProps & {
  content: ContentSectionCustomCard;
};

export type ServerResponse<T, K = {}> = {
  error?: string;
  meta: K;
  payload: {
    results: T;
    filters: {
      q: string | null;
    };
    pagination: {
      page: number;
      per_page: number;
      total_pages: number;
      total_results: number;
    };
    form_fields?: [
      {
        label?: string;
        name?: string;
        error?: string;
      },
    ];
    suggestions?: {
      search?: {
        suggested_queries?: string[];
        comics?: {
          message: string | null;
          results: Comic[];
        };
        releases?: {
          message: string | null;
          results: Release[];
        };
        artists?: {
          message: string | null;
          results: Artist[];
        };
      };
    };
  };
};

export type ServerListResponse<T> = ServerResponse<Array<T>>;
export type ServerSingleResponse<T> = ServerResponse<T>;

export type SectionResultResponse = ServerListResponse<Section>;

export type Option<T> = {
  value: T;
  label: string;
};

export type Country = {
  entity_type: string;
  id: string;
  code: string;
  name: string;
  geo_region_id: number;
  currency_id: number;
  default_lang: string;
  price_includes_tax: number;
  apple_currency_id: number;
  google_currency_id: number;
  flag_url: string;
};

export type User = {
  entity_type: string;
  id: number;
  name: string;
  join_date: string;
  verified: number;
  is_deleted: number;
  user_ban_id: null | number;
  total_posts: number;
  avatar_image_id: number;
  releases_lang: null | string;
  ui_lang?: string;
  first_name: null | string;
  last_name: null | string;
  full_name: null | string;
  display_real_name_in_public: number;
  beta_access: number;
  enable_pms: number;
  description: string;
  website: null | string;
  country_id: string;
  total_orders: number;
  display_donations_in_public: number;
  disable_payments: number;
  total_projects: number;
  onboarding: string;
  hide_ai_results: number;
  show_mature_content: number;
  gold: boolean;
  country: Country;
  nice_join_date: string;
  url: string;
  edit_url: string;
  admin_edit_url: string;
  avatar_url: string;
  avatar_small_url: string;
  avatar_tiny_url: string;
  email_address: string;
  cohort_id: number;
  usergroups: number[];
  is_superadmin: boolean;
  is_admin: boolean;
  is_supporter: boolean;
  user_creator_data: {
    edit_url: string;
    avatar_url: string;
    avatar_small_url: string;
    avatar_tiny_url: string;
    roman_name: string;
    url: string;
  };
  artist_users: ArtistUser[];
  following_count?: number;
  favorites_count?: number;
  total_releases_completed?: number;
  total_pages_read?: number;
};

export type GCChannel = {
  id: string;
  display_name: string;
  display_order: number;
  entity_type: string;
  is_hidden: boolean;
  is_primary: boolean;
  streams: GCStream[];
  config: GCChannelConfig[];
};

export type GCChannelConfig = {
  icon: string;
  theme: string;
  search_context?: {
    browse_key: string;
    filters?: Record<string, number>;
    placeholder?: string;
  };
};

export type GCStream = GCChannel & {
  slug: string;
};

export type Account = {
  entity_type: string;
  user: User | null;
  lists: any[]; // Type could be more specific if known
  subscription: boolean;
  subscription_summary?: {
    is_free_trial?: boolean;
  } | null;
  unseen_notification_count: number;
  account_links: Record<
    string,
    string | { headline: string; sub_head: string; uuid: null }
  >;
  interests: {
    comic_facets: number[]; // Type could be more specific if known
    comic_genres: number[]; // Type could be more specific if known
    comic_types: number[]; // Type could be more specific if known
  };
  archetype: Archetype | null;
  display_subscription_offer_popup: boolean;
  user_offer: null;
  comic_favorites: number[];
  artist_favorites: number[];
  device_uuid: null;
  device_push_permissions_status: null;
  hidden_reading_history_comics: any[]; // Type could be more specific if known
  home_tab_streams: GCStream[];
  home_tab_channels: GCChannel[];
  user_is_creator: boolean;
  can_use_admin_panel: boolean;
  user_creator_data: {
    edit_url: string;
  };
  originals_channel: GCChannel;
  guest_id?: string;
  features: { [key: string]: string | boolean };
  comic_user_reading_statuses: ComicUserReadingStatusEntity[];
  baf_token: AccessTokenEntity;
  flash_messages: FlashMessage[];

  ribbon?: {
    key: string;
    content: string;
    display_name: string;
  };
  offer_paywall?: string;
  deals_stream?: StreamInfo;
  new_stream?: StreamInfo;
};

export type StreamInfo = {
  stream_key: string;
  display_name: string;
  icon: string;
};

export type AccessTokenEntity = {
  entity_type: "AccessToken";
  id: number;
  token: string;
  access_token_type_id: number;
  access_token_campaign_id: number;
  duration: string;
  start_time: string;
  end_time: string;
  original_uses: number;
  remaining_uses: number;
  create_time: string;
  creator_id: number;
  local_start_time: string;
  local_end_time: string;
  duration_name: string;
  access_token_facets: [];
  url: string;
  expires_in: string;
  share_url: string;
  share_message: string;
  flash_messages: FlashMessage[];
};

export type FlashMessage = {
  content: string;
  type_display: "error" | "success" | "information";
};

export type ExtendedAppInit = InitResponsePayloadResults & {
  comic_genres: ComicCategoryEntity[];
};

export type ComicCrowdfundingTypeEntity = {
  id: string;
  name: string;
};

export type DaysOfWeekKeys = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface Page {
  entity_type: string;
  id: number;
  release_id: number;
  comic_asset_id: number;
  order: number;
  force_is_free: number;
  is_active: number;
  update_time: null;
  create_time: Date;
  release_order: number;
  title: string;
  page_count: number;
  comic_id: number;
  lang_id: string;
  comic_asset_file_id: number;
  comic_asset_file_status_id: number;
  comic_asset_file_type_id: number;
  comic_asset_type_id: number;
  desktop_width: number;
  desktop_height: number;
  mobile_width: number;
  mobile_height: number;
  aspect_x: number;
  has_sam_embeddings: number;
  defer_secure_urls: boolean;
  page_id: number;
  is_double: boolean;
  read_url: string;
  edit_url: string;
  settings_url: string;
  nice_filesize: string;
  thumbnail_url: string;
  user_owns_page: boolean;
  is_page_paid: boolean;
  display_paid_access_block: boolean;
  is_augmented: number;
  desktop_image_url: string;
  mobile_image_url: string;
  is_double_with_next: boolean;
  is_double_with_prev: boolean;
}

export interface ComicLang {
  entity_type: string;
  id: number;
  comic_id: number;
  lang_id: string;
  name: string;
  description: string;
  comic_update_frequency: null;
  comic_update_period: null;
  is_augmented: number;
  first_release_order: number;
  featured_date: null;
  count_for_sale: number;
  count_gold: number;
  latest_release_id: number;
  first_published_release_time: Date;
  first_release_uuid: string;
  last_published_release_time: Date;
  original_first_published_release_time: null;
  free_release_count: number;
  total_published_releases: number;
  total_published_pages: number;
  pages_viewed: number;
  is_active: number;
}

export interface Fragment {
  entity_type: string;
  id: number;
  page_id: number;
  release_id: number;
  suggested_fragment_id: null;
  top: number;
  left: number;
  width: number;
  height: number;
  order: number;
  path: string;
  options: string;
}

export type ConversationEntity = {
  entity_type: string;
  id: number;
  thread_id: number;
  last_post_date: string;
  last_post_time_ago: string;
  is_read: number;
  name: string;
  body: string;
  last_author: ConversationUser;
};

export type BlogCommentEntity = {
  entity_type: string;
  id: number;
  object_id: number;
  parent_id?: number;
  total_comments: number;
  level: number;
  page_id: null;
  body: string;
  parsed_body: string;
  author_id: number;
  post_date: string;
  edit_user_id: null;
  edit_date: null;
  is_deleted: number;
  artist_id: number;
  pos: null;
  comment_type: string;
  can_edit: boolean;
  can_moderate: boolean;
  time_ago: string;
  edit_time_ago: null;
  url: string;
  delete_url: string;
  edit_url: string;
  author_is_context_owner: boolean;
  replies: BlogCommentEntity[];
  author: User;

  total_good: number;
  total_bad: number;
  user_vote_reaction_type_id: number | null;
};

export interface Price {
  entity_type: string;
  id: number;
  price_type_id: number;
  entity_type_id?: number;
  entity_id?: number;
  currency_id: number;
  incentive_id?: null;
  price_int: number;
  allow_variable_max_price: number;
  is_active?: number;
  artist_id?: number;
  currency?: Currency;
  price_int_discounted: number | null;
  display_price: string;
  display_price_discounted?: string;
  price_float: number;
  price_float_discounted?: number;
  is_for_sale: boolean;
  is_on_sale: boolean;
  is_discounted: boolean;
  price_full_int: number;
  price_full_float: number;
  price_type?: PriceType;
}

export interface Currency {
  entity_type: string;
  id: number;
  name: string;
  display_name: string;
  symbol: string;
  decimals: number;
  is_active: number;
}

export interface PriceType {
  entity_type: string;
  id: number;
  display_name: string;
  offer_id: null;
  is_active: number;
}

export type BundlePurchasePayload = {
  price_point_id: number;
  full_price: number;
  country_code?: string;
  currency_code: string;
  raw_price: number | undefined;
  is_gold: boolean;
  is_sale: boolean;
  stripeToken?: string;
  ownerPaymentServiceTokenId?: number;
  [key: string]: any; // For dynamic raw_price_* fields
};

export interface ReleaseUserPurchase {
  entity_type: string;
  id: number;
  token: string;
  release_id: number;
  user_id: number;
  session_id: number;
  net_price: string;
  is_downloadable: number;
  is_active: number;
  creator_id: number;
  updater_id: number;
  update_time: Date;
  create_time: Date;
  download_url: string;
  can_moderate: boolean;
}

export interface ApplicationUserAccessToken {
  id: number;
  token: string;
  expires_on: string;
  user_id: number;
}

export interface AttributionParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  etId: string | null;
  original_ref: string | null;
}

export interface NavigationSource {
  sourceChannelSlug?: string;
  sourceSlug?: string;
  sourceChannelName: string;
  sourcePath: string;
}

export type Archetype = {
  id: number;
  slug: string;
  display_name: string;
  description: string;
  image_url: string;
};
